"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AnalystMindStage, {
  ANALYST_MIND_DURATION_MS,
} from "./AnalystMindStage";
import BastionSystemStage, {
  BASTION_SYSTEM_DURATION_MS,
} from "./BastionSystemStage";
import MethodStaticInfographic from "./MethodStaticInfographic";
import styles from "./how-it-works.module.css";

const SCENES = [
  { duration: ANALYST_MIND_DURATION_MS },
  { duration: BASTION_SYSTEM_DURATION_MS },
] as const;

const INTRO_HOLD_MS = 1_000;
const DURATION_MS =
  INTRO_HOLD_MS + SCENES.reduce((total, scene) => total + scene.duration, 0);
const SCENE_OFFSETS = SCENES.map((_, index) =>
  SCENES.slice(0, index).reduce((total, scene) => total + scene.duration, 0),
);

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function sceneFromElapsed(ms: number) {
  const t = Math.max(
    0,
    Math.min(DURATION_MS - INTRO_HOLD_MS, ms - INTRO_HOLD_MS),
  );

  for (let index = 0; index < SCENES.length; index += 1) {
    const start = SCENE_OFFSETS[index];
    const end = start + SCENES[index].duration;
    if (t < end || index === SCENES.length - 1) {
      return { index };
    }
  }

  return { index: 0 };
}

export default function AnalystWorkflowStage() {
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(false);
  const stageRef = useRef<HTMLElement | null>(null);
  const elapsedRef = useRef(0);
  const lastTsRef = useRef(0);
  // Tracks an explicit user pause (click) so scrolling back into view does not
  // override the user's choice by auto-resuming.
  const manualPauseRef = useRef(false);

  elapsedRef.current = elapsed;

  const { index: sceneIndex } = useMemo(
    () => sceneFromElapsed(elapsed),
    [elapsed],
  );
  const animationElapsed = Math.max(0, elapsed - INTRO_HOLD_MS);
  const mindElapsed = Math.min(ANALYST_MIND_DURATION_MS, animationElapsed);
  const systemElapsed = Math.min(
    BASTION_SYSTEM_DURATION_MS,
    Math.max(0, animationElapsed - ANALYST_MIND_DURATION_MS),
  );

  const startFrom = useCallback((ms: number) => {
    if (prefersReducedMotion()) {
      setElapsed(DURATION_MS);
      setPlaying(false);
      return;
    }

    setElapsed(ms);
    elapsedRef.current = ms;
    lastTsRef.current = 0;
    setPlaying(true);
  }, []);

  const togglePlay = useCallback(() => {
    if (prefersReducedMotion()) {
      setElapsed(DURATION_MS);
      setPlaying(false);
      return;
    }

    // Finished: a click replays from the beginning.
    if (elapsedRef.current >= DURATION_MS) {
      manualPauseRef.current = false;
      startFrom(0);
      return;
    }

    // Otherwise a click toggles play / pause. Remember a manual pause.
    setPlaying((current) => {
      const next = !current;
      manualPauseRef.current = !next;
      return next;
    });
  }, [startFrom]);

  useEffect(() => {
    if (!playing) {
      lastTsRef.current = 0;
      return;
    }

    let frame = 0;
    const loop = (now: number) => {
      if (!lastTsRef.current) lastTsRef.current = now;
      const next = Math.min(
        DURATION_MS,
        elapsedRef.current + (now - lastTsRef.current),
      );
      lastTsRef.current = now;
      elapsedRef.current = next;
      setElapsed(next);

      if (next >= DURATION_MS) {
        setPlaying(false);
        return;
      }

      frame = requestAnimationFrame(loop);
    };

    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [playing]);

  // Auto-play when the stage scrolls into view; pause when it scrolls away and
  // resume from where it left off when it comes back (unless the user paused it).
  useEffect(() => {
    const target = stageRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (prefersReducedMotion()) {
            setElapsed(DURATION_MS);
            return;
          }
          if (elapsedRef.current >= DURATION_MS) return; // finished: hold final frame
          if (manualPauseRef.current) return; // respect an explicit pause
          if (elapsedRef.current === 0) {
            startFrom(0);
          } else {
            lastTsRef.current = 0;
            setPlaying(true);
          }
        } else {
          // Scrolled out of view: pause, keep progress (not a manual pause).
          setPlaying(false);
        }
      },
      { threshold: 0.3 },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [startFrom]);

  const finished = elapsed >= DURATION_MS;
  const controlLabel = playing
    ? "Pause animation"
    : finished
      ? "Replay animation"
      : "Play animation";

  return (
    <section className={styles.mindSection} id="analyst-workflow" ref={stageRef}>
      {/* Wide screens: the animated "noise -> signal" stage. */}
      <div
        className={`${styles.mindFrame} ${styles.methodAnimated}`}
        role="button"
        tabIndex={0}
        aria-label={controlLabel}
        style={{ cursor: "pointer" }}
        onClick={togglePlay}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            togglePlay();
          }
        }}
      >
        <div className={styles.storyScenes}>
          <div
            className={`${styles.storyScene} ${
              sceneIndex === 0 ? styles.storySceneActive : ""
            }`}
          >
            <AnalystMindStage
              embedded
              elapsed={mindElapsed}
              statusLabels={[
                "Forming the analyst's thinking",
                "From analyst model to investor workflows",
              ]}
            />
          </div>
          <div
            className={`${styles.storyScene} ${
              sceneIndex === 1 ? styles.storySceneActive : ""
            }`}
          >
            <BastionSystemStage embedded hideAI elapsed={systemElapsed} />
          </div>
        </div>
      </div>

      {/* Narrow screens: static vertical infographic, no animation. */}
      <div className={styles.methodStatic}>
        <MethodStaticInfographic />
      </div>

      <span className={styles.srStatus} aria-live="polite">
        {playing
          ? "Playing"
          : elapsed === 0
            ? "Animation stopped"
            : finished
              ? "Animation complete"
              : "Animation paused"}
      </span>
    </section>
  );
}
